import { CONSENT_VERSION, RESEARCH_CONSENT_KEY } from './constants';
import { randomId } from './ids';
import type { ConsentRecord } from './types';

function blankConsent(participantId?: string): ConsentRecord {
  return {
    granted: false,
    version: CONSENT_VERSION,
    grantedAt: null,
    revokedAt: null,
    prompted: false,
    participantId: participantId ?? randomId(),
  };
}

export function parseConsentRecord(raw: unknown): ConsentRecord | null {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const rec = raw as Partial<ConsentRecord>;
  if (typeof rec.participantId !== 'string' || rec.participantId.length < 8) {
    return null;
  }
  return {
    granted: rec.granted === true,
    version: typeof rec.version === 'string' ? rec.version : '',
    grantedAt: typeof rec.grantedAt === 'number' ? rec.grantedAt : null,
    revokedAt: typeof rec.revokedAt === 'number' ? rec.revokedAt : null,
    prompted: rec.prompted === true,
    participantId: rec.participantId,
  };
}

export function loadConsent(): ConsentRecord {
  try {
    const raw = localStorage.getItem(RESEARCH_CONSENT_KEY);
    if (!raw) {
      return blankConsent();
    }
    const parsed = parseConsentRecord(JSON.parse(raw) as unknown);
    if (!parsed) {
      return blankConsent();
    }
    return parsed;
  } catch {
    return blankConsent();
  }
}

export function saveConsent(record: ConsentRecord): void {
  try {
    localStorage.setItem(RESEARCH_CONSENT_KEY, JSON.stringify(record));
  } catch {
    // private mode / quota
  }
}

export function hasActiveConsent(nowVersion = CONSENT_VERSION): boolean {
  const rec = loadConsent();
  return rec.granted && rec.version === nowVersion;
}

export function needsConsentPrompt(nowVersion = CONSENT_VERSION): boolean {
  const rec = loadConsent();
  if (rec.granted && rec.version === nowVersion) {
    return false;
  }
  if (!rec.prompted) {
    return true;
  }
  return rec.version !== nowVersion;
}

export function getParticipantId(): string {
  const rec = loadConsent();
  if (!rec.participantId) {
    const next = blankConsent();
    saveConsent(next);
    return next.participantId;
  }
  if (!localStorage.getItem(RESEARCH_CONSENT_KEY)) {
    saveConsent(rec);
  }
  return rec.participantId;
}

export function grantConsent(at = Date.now()): ConsentRecord {
  const rec = loadConsent();
  const next: ConsentRecord = {
    ...rec,
    granted: true,
    version: CONSENT_VERSION,
    grantedAt: at,
    revokedAt: null,
    prompted: true,
    participantId: rec.participantId || randomId(),
  };
  saveConsent(next);
  return next;
}

export function revokeConsent(at = Date.now()): ConsentRecord {
  const rec = loadConsent();
  const next: ConsentRecord = {
    ...rec,
    granted: false,
    version: CONSENT_VERSION,
    revokedAt: at,
    prompted: true,
  };
  saveConsent(next);
  return next;
}

export function markConsentPrompted(): ConsentRecord {
  const rec = loadConsent();
  const next: ConsentRecord = {
    ...rec,
    prompted: true,
    version: rec.version || CONSENT_VERSION,
  };
  saveConsent(next);
  return next;
}

export function declineConsent(at = Date.now()): ConsentRecord {
  const rec = loadConsent();
  const next: ConsentRecord = {
    ...rec,
    granted: false,
    version: CONSENT_VERSION,
    revokedAt: rec.granted ? at : rec.revokedAt,
    prompted: true,
  };
  saveConsent(next);
  return next;
}

export function resetConsentAfterDelete(): ConsentRecord {
  const next = blankConsent();
  next.prompted = true;
  saveConsent(next);
  return next;
}

export function clearConsentStorage(): void {
  try {
    localStorage.removeItem(RESEARCH_CONSENT_KEY);
  } catch {
    // ignore
  }
}
